// ประวัติการแก้ไขข้อมูลสำคัญ — อ่านอย่างเดียว ไม่มีทางแก้หรือลบจากหน้านี้
//
// ต่างจากหน้า สถิติการใช้งาน ที่ตอบว่า "ใครเข้าระบบ ใช้อะไรบ้าง"
// หน้านี้ตอบว่า "ใครแก้คะแนนของใคร จากเท่าไหร่เป็นเท่าไหร่ เมื่อไหร่"
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { StatTile, num } from "./usage/shared";

const API = process.env.REACT_APP_API;

const RANGE_OPTIONS = [
  { value: 7,    label: '7 วันล่าสุด' },
  { value: 30,   label: '30 วันล่าสุด' },
  { value: 90,   label: '90 วันล่าสุด' },
  { value: 365,  label: '1 ปีล่าสุด' },
  { value: 1095, label: 'ทั้งหมดที่เก็บไว้' },
];

// ป้ายและสีของแต่ละการกระทำ — แดงคือสิ่งที่ต้องสะดุดตาเวลามีเรื่อง
const ACTIONS = {
  'grade.update':    { label: 'แก้เกรด',            variant: 'danger'  },
  'grade.create':    { label: 'กรอกเกรดครั้งแรก',   variant: 'success' },
  'grade.clear':     { label: 'ลบเกรด',             variant: 'danger'  },
  'grade.waiting':   { label: 'เปลี่ยนสถานะ ร',      variant: 'warning' },
  'score.update':    { label: 'แก้คะแนนเก็บ',        variant: 'danger'  },
  'score.create':    { label: 'กรอกคะแนนครั้งแรก',   variant: 'success' },
  'score.clear':     { label: 'ลบคะแนนเก็บ',        variant: 'danger'  },
  'password.reset':  { label: 'รีเซ็ตรหัสผ่าน',      variant: 'warning' },
  'account.disable': { label: 'ปิดใช้งานบัญชี',      variant: 'danger'  },
  'account.enable':  { label: 'เปิดใช้งานบัญชี',     variant: 'info'    },
  'position.grant':  { label: 'ให้สิทธิ์ตำแหน่ง',     variant: 'warning' },
  'position.revoke': { label: 'ถอนสิทธิ์ตำแหน่ง',    variant: 'warning' },
  'attendance.update': { label: 'แก้เช็คชื่อย้อนหลัง', variant: 'danger'  },
  'student.status':  { label: 'เปลี่ยนสถานะนักเรียน', variant: 'warning' },
  'student.exit.update': { label: 'แก้ข้อมูลการจำหน่าย', variant: 'danger' },
  'conduct.deduct':  { label: 'หักคะแนนความประพฤติ',  variant: 'warning' },
  'merit.status':    { label: 'เปลี่ยนสถานะจิตอาสา',  variant: 'info'    },
  'merit.delete':    { label: 'ลบงานจิตอาสา',        variant: 'danger'  },
  'invoice.cancel':  { label: 'ยกเลิกใบแจ้งหนี้',     variant: 'danger'  },
  'payment.record':  { label: 'รับชำระเงิน',          variant: 'success' },
  'admission.create': { label: 'รับสมัครนักเรียน',    variant: 'success' },
  'admission.update': { label: 'แก้ข้อมูลผู้สมัคร',    variant: 'danger'  },
  'gate.device.create': { label: 'เพิ่มอุปกรณ์ที่ประตู',  variant: 'success' },
  'gate.device.delete': { label: 'ลบอุปกรณ์ที่ประตู',    variant: 'danger'  },
  'gate.device.status': { label: 'เปิด/ปิดอุปกรณ์ที่ประตู', variant: 'warning' },
  'gate.device.token':  { label: 'ออก token กล่องประตู',  variant: 'danger'  },
  'nurse.visit':     { label: 'แก้/ลบบันทึกห้องพยาบาล', variant: 'danger'  },
  'student.health':  { label: 'แก้ข้อมูลสุขภาพนักเรียน', variant: 'warning' },
};

const actionInfo = (action) => ACTIONS[action] || { label: action, variant: 'secondary' };

const PAGE_SIZE = 200;

const ATT_LABEL   = { present: 'มา', absent: 'ขาด', late: 'สาย', leave: 'ลา' };
const MERIT_LABEL = { assigned: 'มอบหมาย', completed: 'ทำแล้ว', cancelled: 'ยกเลิก' };
const EXIT_TYPE_LABEL = { graduate: 'สำเร็จการศึกษา', withdraw: 'จำหน่าย' };

// mysql2 คืน JSON column มาเป็น object แล้ว แต่บาง driver/รุ่นคืนเป็น string — รับทั้งสองแบบ
const parseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
};

// ค่าที่คนอ่านต้องเห็น: คะแนนคือตัวเลข ส่วนสถานะ ร แปลงเป็นคำ
const describeValue = (obj) => {
  if (!obj) return '—';
  if ('sm_score' in obj) return obj.sm_score === null ? 'ว่าง' : String(obj.sm_score);
  if ('g_grade' in obj) {
    const score = obj.g_grade === null ? 'ว่าง' : String(obj.g_grade);
    return String(obj.g_waiting) === '1' ? `${score} (ติด ร)` : score;
  }
  if ('t_status' in obj) return String(obj.t_status) === '1' ? 'ใช้งาน' : 'ปิดใช้';
  if ('att_status' in obj) return ATT_LABEL[obj.att_status] || String(obj.att_status);
  if ('st_status' in obj) return String(obj.st_status);
  // แก้ข้อมูลการจำหน่าย — เก็บเฉพาะช่องที่เปลี่ยนจริง จึงต้องพิมพ์เท่าที่มีในก้อนนั้น
  if ('ex_date' in obj || 'ex_type' in obj || 'ex_reason' in obj || 'ex_destination' in obj) {
    return [EXIT_TYPE_LABEL[obj.ex_type] || obj.ex_type, obj.ex_date, obj.ex_reason,
            obj.ex_destination, obj.ex_note].filter(Boolean).join(' · ') || 'ว่าง';
  }
  if ('status' in obj) return `${MERIT_LABEL[obj.status] || obj.status} (+${obj.add_points ?? 0})`;
  if ('deduct_points' in obj) return `-${obj.deduct_points}`;
  if ('pay_amount' in obj) return `${obj.pay_amount} บาท`;
  if ('invoice_status' in obj) return `${obj.invoice_status} (คงค้าง ${obj.balance})`;
  return JSON.stringify(obj);
};

export default function AdminAuditLog() {
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState({ byAction: [], topActors: [] });
  const [days, setDays]       = useState(30);
  const [action, setAction]   = useState('all');
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded]   = useState(0);

  // ค่าเริ่มต้นคือซ่อนการกรอกครั้งแรก เพราะการกรอกคะแนนครั้งแรกมีเป็นหลักหมื่นแถวต่อเทอม
  // (ตอนนี้ tb_scoremark มีสามหมื่นช่อง) ถ้าโชว์ปนมาด้วย การแก้ไขที่ต้องสืบจริงจะจมหาย
  const [changesOnly, setChangesOnly] = useState(true);

  const fetchPage = useCallback((offset, append) => {
    setBusy(true);
    return axiosInstance
      .get(`${API}/admin/audit`, {
        params: {
          days,
          action: action === 'all' ? undefined : action,
          search: search.trim() || undefined,
          changesOnly: changesOnly ? 1 : undefined,
          limit: PAGE_SIZE,
          offset,
        },
      })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setRows((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PAGE_SIZE);
        setLoaded(offset + data.length);
      })
      .catch(() => {
        if (!append) { setRows([]); setHasMore(false); setLoaded(0); }
      })
      .finally(() => setBusy(false));
  }, [days, action, search, changesOnly]);

  // เปลี่ยนตัวกรอง = เริ่มนับหนึ่งใหม่ ไม่ใช่ต่อท้ายของเดิม
  // หน่วงเวลาไว้ เพื่อไม่ให้ยิง request ทุกตัวอักษรที่พิมพ์ค้นหา
  useEffect(() => {
    const timer = setTimeout(() => fetchPage(0, false), 300);
    return () => clearTimeout(timer);
  }, [fetchPage]);

  // ยอดสรุปขึ้นกับช่วงเวลาอย่างเดียว ไม่ต้องโหลดใหม่ตอนกดโหลดเพิ่มหรือเปลี่ยนตัวกรองอื่น
  useEffect(() => {
    axiosInstance
      .get(`${API}/admin/audit/summary`, { params: { days } })
      .then((res) => setSummary(res.data || { byAction: [], topActors: [] }))
      .catch(() => setSummary({ byAction: [], topActors: [] }));
  }, [days]);

  const reload = () => fetchPage(0, false);

  const countOf = (prefix) =>
    summary.byAction
      .filter((r) => String(r.action).startsWith(prefix))
      .reduce((sum, r) => sum + num(r.total), 0);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-4">
          <h4 className="main-title mb-1">ประวัติการแก้ไขข้อมูล</h4>
          <p className="text-secondary mb-0 fs-sm">
            ใครแก้อะไร จากค่าอะไรเป็นค่าอะไร — บันทึกเฉพาะเมื่อค่าเปลี่ยนจริง
          </p>
        </div>

        <Row className="g-2 g-lg-3 mb-3">
          <StatTile
            label="แก้เกรด / คะแนนเก็บ"
            value={(countOf('grade.') + countOf('score.')).toLocaleString()}
            variant="danger"
          />
          <StatTile label="แก้เช็คชื่อย้อนหลัง" value={countOf('attendance.').toLocaleString()} variant="warning" />
          <StatTile
            label="คะแนนความประพฤติ"
            value={(countOf('conduct.') + countOf('merit.')).toLocaleString()}
            variant="info"
          />
          <StatTile
            label="บัญชี / สิทธิ์ / การเงิน"
            value={(countOf('account.') + countOf('password.') + countOf('position.')
                  + countOf('invoice.') + countOf('payment.')).toLocaleString()}
            variant="dark"
          />
        </Row>

        <Row className="g-2 g-lg-3 mb-3">
          <Col lg={8}>
            <Card className="card-one">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Form.Control
                  type="search"
                  placeholder="ค้นหาชื่อผู้แก้ / ชื่อนักเรียน / รหัส"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
                <Form.Select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 170 }}>
                  {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
                <Form.Select value={action} onChange={(e) => setAction(e.target.value)} style={{ maxWidth: 210 }}>
                  <option value="all">การกระทำ: ทั้งหมด</option>
                  {Object.entries(ACTIONS).map(([key, info]) => (
                    <option key={key} value={key}>{info.label}</option>
                  ))}
                </Form.Select>
                <Form.Check
                  type="switch"
                  id="changesOnly"
                  label="เฉพาะการแก้ไข"
                  title="ซ่อนการกรอกคะแนน/เกรดครั้งแรก ซึ่งเป็นการทำงานปกติ"
                  checked={changesOnly}
                  disabled={action !== 'all'}
                  onChange={(e) => setChangesOnly(e.target.checked)}
                />
                <Button size="sm" variant="outline-primary" className="ms-auto" onClick={reload} disabled={busy}>
                  <i className="ri-refresh-line me-1"></i>รีเฟรช
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-secondary">
                      <tr>
                        <th style={{ width: 130 }}>วัน-เวลา</th>
                        <th style={{ width: 160 }}>การกระทำ</th>
                        <th>ผู้แก้</th>
                        <th>เกี่ยวกับ</th>
                        <th style={{ width: 170 }}>จาก → เป็น</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
                      ) : rows.map((r) => {
                        const info   = actionInfo(r.al_action);
                        const before = parseJson(r.al_before);
                        const after  = parseJson(r.al_after);
                        return (
                          <tr key={r.al_id}>
                            <td className="align-middle fs-sm text-nowrap">{r.al_time_text}</td>
                            <td className="align-middle">
                              <Badge bg={info.variant}>{info.label}</Badge>
                            </td>
                            <td className="align-middle fs-sm">
                              {r.actor_name || <span className="text-secondary">ระบบ</span>}
                            </td>
                            <td className="align-middle fs-sm">
                              {r.student_name
                                ? <>{r.student_name} <span className="text-secondary">({r.student_idnumber})</span></>
                                : r.target_name
                                  ? <>{r.target_name} <span className="text-secondary">({r.target_idnumber})</span></>
                                  : <span className="text-secondary">—</span>}
                              {r.al_note && <div className="text-secondary fs-xs">{r.al_note}</div>}
                            </td>
                            <td className="align-middle fs-sm text-nowrap">
                              {before || after ? (
                                <>
                                  <span className="text-secondary">{describeValue(before)}</span>
                                  <i className="ri-arrow-right-line mx-1"></i>
                                  <span className="fw-semibold">{describeValue(after)}</span>
                                </>
                              ) : <span className="text-secondary">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="d-flex align-items-center gap-2">
                <span className="text-secondary fs-sm">
                  แสดง {rows.length.toLocaleString()} รายการ
                  {changesOnly && action === 'all' && ' (ไม่รวมการกรอกครั้งแรก)'}
                </span>
                {hasMore && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ms-auto"
                    onClick={() => fetchPage(loaded, true)}
                    disabled={busy}
                  >
                    โหลดเพิ่มอีก {PAGE_SIZE}
                  </Button>
                )}
              </Card.Footer>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="card-one h-100">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">ผู้ที่แก้ข้อมูลมากที่สุด</Card.Title>
                <span className="text-secondary fs-xs">
                  แก้เยอะไม่ได้แปลว่าผิด ครูที่รับผิดชอบหลายห้องย่อมมีมากเป็นธรรมดา
                </span>
              </Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: 460, overflowY: 'auto' }}>
                <Table hover className="mb-0">
                  <tbody>
                    {summary.topActors.length === 0 ? (
                      <tr><td className="text-center text-secondary py-4">ยังไม่มีข้อมูล</td></tr>
                    ) : summary.topActors.map((r) => (
                      <tr key={r.al_actor_t_id}>
                        <td className="fs-sm">
                          {r.t_name}
                          <div className="text-secondary fs-xs">{r.t_idnumber}</div>
                        </td>
                        <td className="text-end align-middle text-nowrap">{num(r.total).toLocaleString()} ครั้ง</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <p className="text-secondary fs-xs mb-0">
          <i className="ri-lock-line me-1"></i>
          ประวัตินี้แก้ไขหรือลบจากในระบบไม่ได้ — ถูกลบอัตโนมัติเมื่อเกินอายุการเก็บที่กำหนดใน
          <code className="mx-1">AUDIT_RETENTION_DAYS</code> (ค่าเริ่มต้น 3 ปี) เท่านั้น
        </p>
      </div>
    </React.Fragment>
  );
}
